/**
 * 版本管理服務
 * 透過 /version.json 檢查更新，確保使用者重整後取得新版本
 * 當前版本以 APP_VERSION（打包時寫入）為單一來源，避免與設定頁顯示不一致
 */

import { APP_VERSION } from '../generated/version';

export interface VersionInfo {
  version: string;
  buildTime: string;
  timestamp: number;
}

export interface VersionComparisonResult {
  hasUpdate: boolean;
  currentVersion: string;
  latestVersion: string;
  updateType: 'major' | 'minor' | 'patch';
  forceUpdate: boolean;
  minVersion?: string;
  isMinVersionForced?: boolean;
}

const VERSION_CHECK_KEY = 'lastVersionCheck';
const CURRENT_VERSION_KEY = 'currentAppVersion';
const PWA_VERSION_KEY = 'currentPWAVersion';
const WEB_VERSION_KEY = 'currentWebVersion';
const CHECK_INTERVAL = 30 * 1000;

class VersionService {
  private getVersionKey(): string {
    return this.isPWAMode() ? PWA_VERSION_KEY : WEB_VERSION_KEY;
  }

  /** 一律回傳此 bundle 的版本（與設定頁 APP_VERSION 一致），不依賴 localStorage 避免亂跳 */
  private getCurrentVersion(): string {
    return APP_VERSION;
  }

  private setCurrentVersion(version: string): void {
    localStorage.setItem(this.getVersionKey(), version);
  }

  private isPWAMode(): boolean {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      (window.navigator as any).standalone === true ||
      document.referrer.includes('android-app://')
    );
  }

  private async fetchRemoteVersion(): Promise<VersionInfo | null> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-cache',
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });

      clearTimeout(timeoutId);
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  private compareVersions(
    current: string,
    latest: string,
    _minVersion?: string
  ): VersionComparisonResult {
    const curr = current.split('.').map(Number);
    const lat = latest.split('.').map(Number);
    while (curr.length < 3) curr.push(0);
    while (lat.length < 3) lat.push(0);

    let updateType: 'major' | 'minor' | 'patch' = 'patch';
    let hasUpdate = false;
    let forceUpdate = false;

    if (lat[0] > curr[0]) {
      hasUpdate = true;
      updateType = 'major';
      forceUpdate = true;
    } else if (lat[0] === curr[0] && lat[1] > curr[1]) {
      hasUpdate = true;
      updateType = 'minor';
      forceUpdate = true;
    } else if (lat[0] === curr[0] && lat[1] === curr[1] && lat[2] > curr[2]) {
      hasUpdate = true;
      updateType = 'patch';
    }

    return {
      hasUpdate,
      currentVersion: current,
      latestVersion: latest,
      updateType,
      forceUpdate,
    };
  }

  async checkForUpdates(): Promise<VersionComparisonResult | null> {
    try {
      const remote = await this.fetchRemoteVersion();
      if (!remote) return null;

      const local = this.getCurrentVersion();
      const comparison = this.compareVersions(local, remote.version);

      return comparison.hasUpdate ? comparison : null;
    } catch {
      return null;
    }
  }

  async clearAppCache(): Promise<void> {
    try {
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }

      const keysToKeep = [
        'userAcceptedInstall',
        'runningAsPWA',
        CURRENT_VERSION_KEY,
        PWA_VERSION_KEY,
        WEB_VERSION_KEY,
        VERSION_CHECK_KEY,
      ];

      Object.keys(localStorage).forEach((key) => {
        if (!keysToKeep.includes(key) && !key.startsWith('firebase:')) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.error('清除緩存失敗:', e);
    }
  }

  async performUpdate(newVersion: string): Promise<void> {
    this.setCurrentVersion(newVersion);
    await this.clearAppCache();
  }

  forceReload(): void {
    window.location.href = `${window.location.origin}${window.location.pathname}?v=${Date.now()}`;
  }

  private isRecentlyChecked(): boolean {
    const last = localStorage.getItem(VERSION_CHECK_KEY);
    if (!last) return false;
    return Date.now() - parseInt(last, 10) < CHECK_INTERVAL;
  }

  markVersionChecked(): void {
    localStorage.setItem(VERSION_CHECK_KEY, Date.now().toString());
  }

  async initialize(): Promise<VersionComparisonResult | null> {
    try {
      if (this.isRecentlyChecked()) return null;

      const result = await this.checkForUpdates();
      this.markVersionChecked();
      return result;
    } catch {
      return null;
    }
  }
}

export const versionService = new VersionService();
export default versionService;
